"use client"

import { useEffect, useState } from "react"
import {
  getRecommendations,
  getPopularBooks,
  addFavorite,
  deleteFavorite,
  addOrUpdateRating,
  getFavorites,
  getRatings
} from "../services/api"
import BookCard from "../components/BookCard"
import "./RecommendedPage.css"

function RecommendedPage() {
  const [userId, setUserId] = useState(null)
  const [username, setUsername] = useState(null)
  const [recommendations, setRecommendations] = useState([])
  const [popularBooks, setPopularBooks] = useState([])
  const [favorites, setFavorites] = useState([])
  const [ratings, setRatings] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const storedUserId = localStorage.getItem("userId")
    const storedUsername = localStorage.getItem("username")

    setUserId(storedUserId)
    setUsername(storedUsername)

    if (storedUserId) {
      fetchBooks(storedUserId)
    } else {
      fetchBooks(null)
    }
  }, [])

  const fetchBooks = async (userId) => {
    setLoading(true)
    setError(null)

    try {
      const [popularData, recommendationsData, favoritesData, ratingsData] = await Promise.all([
        getPopularBooks(8),
        userId ? getRecommendations(userId) : Promise.resolve([]),
        userId ? getFavorites(userId) : Promise.resolve([]),
        userId ? getRatings(userId) : Promise.resolve([]),
      ])

      setPopularBooks(popularData)
      setRecommendations(recommendationsData)
      console.log("Recommendations:", recommendationsData)

      const favIds = favoritesData.map(book => book.id)
      const ratingMap = {}
      ratingsData.forEach((book) => {
        ratingMap[book.id] = book.userRating
      })

      setFavorites(favIds)
      setRatings(ratingMap)
    } catch (err) {
      console.error("Error loading books:", err)
      setError("Something went wrong while loading books.")
    } finally {
      setLoading(false)
    }
  }

  const handleFavorite = async (book) => {
    if (!userId) {
      alert("Please log in to favorite books.")
      return
    }

    const isFav = favorites.includes(book.id)
    try {
      if (isFav) {
        await deleteFavorite(userId, book.id)
        setFavorites((prev) => prev.filter((id) => id !== book.id))
      } else {
        await addFavorite(userId, book)
        setFavorites((prev) => [...prev, book.id])
      }
    } catch (err) {
      console.error("Failed to update favorite:", err)
    }
  }

  const handleRating = async (book, ratingValue) => {
    if (!userId) {
      alert("Please log in to rate books.")
      return
    }

    try {
      await addOrUpdateRating(userId, book, ratingValue)
      setRatings((prev) => ({ ...prev, [book.id]: ratingValue }))
    } catch (err) {
      console.error("Failed to update rating:", err)
    }
  }

  // Skeleton UI for loading state
  if (loading) {
    return (
      <div className="recommended-page">
        <div className="recommended-container">
          <div className="page-header skeleton">
            <div className="skeleton-title skeleton-element">
              <div className="shimmer-wrapper">
                <div className="shimmer"></div>
              </div>
            </div>
            <div className="skeleton-subtitle skeleton-element">
              <div className="shimmer-wrapper">
                <div className="shimmer"></div>
              </div>
            </div>
          </div>

          <section className="recommendation-section">
            <div className="skeleton-section-title skeleton-element">
              <div className="shimmer-wrapper">
                <div className="shimmer"></div>
              </div>
            </div>
            <div className="skeleton-section-description skeleton-element">
              <div className="shimmer-wrapper">
                <div className="shimmer"></div>
              </div>
            </div>
            <div className="books-grid">
              {Array(4).fill().map((_, i) => (
                <div key={`skeleton-card-1-${i}`} className="skeleton-book-card skeleton-element">
                  <div className="skeleton-book-cover">
                    <div className="shimmer-wrapper">
                      <div className="shimmer"></div>
                    </div>
                  </div>
                  <div className="skeleton-book-info">
                    <div className="skeleton-book-title skeleton-element">
                      <div className="shimmer-wrapper">
                        <div className="shimmer"></div>
                      </div>
                    </div>
                    <div className="skeleton-book-author skeleton-element">
                      <div className="shimmer-wrapper">
                        <div className="shimmer"></div>
                      </div>
                    </div>
                    <div className="skeleton-book-rating skeleton-element">
                      <div className="shimmer-wrapper">
                        <div className="shimmer"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="recommendation-section">
            <div className="skeleton-section-title skeleton-element">
              <div className="shimmer-wrapper">
                <div className="shimmer"></div>
              </div>
            </div>
            <div className="skeleton-section-description skeleton-element">
              <div className="shimmer-wrapper">
                <div className="shimmer"></div>
              </div>
            </div>
            <div className="books-grid">
              {Array(8).fill().map((_, i) => (
                <div key={`skeleton-card-2-${i}`} className="skeleton-book-card skeleton-element">
                  <div className="skeleton-book-cover">
                    <div className="shimmer-wrapper">
                      <div className="shimmer"></div>
                    </div>
                  </div>
                  <div className="skeleton-book-info">
                    <div className="skeleton-book-title skeleton-element">
                      <div className="shimmer-wrapper">
                        <div className="shimmer"></div>
                      </div>
                    </div>
                    <div className="skeleton-book-author skeleton-element">
                      <div className="shimmer-wrapper">
                        <div className="shimmer"></div>
                      </div>
                    </div>
                    <div className="skeleton-book-rating skeleton-element">
                      <div className="shimmer-wrapper">
                        <div className="shimmer"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    )
  }

  return (
    <div className="recommended-page">
      <div className="recommended-container">
        <div className="page-header">
          <h1 className="page-title">Recommended for You</h1>
          {username ? (
            <p className="page-subtitle">Personalized book recommendations for {username}</p>
          ) : (
            <p className="page-subtitle">Sign in to get personalized recommendations</p>
          )}
        </div>

        {error && <div className="error-message"><p>{error}</p></div>}

        {recommendations.length > 0 && (
          <section className="recommendation-section">
            <h2 className="section-title">Based on Your Taste</h2>
            <p className="section-description">Books recommended based on your favorites and ratings</p>
            <div className="books-grid">
              {recommendations.slice(0, 8).map((book) => (
                <BookCard
                  key={book.id}
                  book={book}
                  userRating={ratings[book.id] || 0}
                  isFavorite={favorites.includes(book.id)}
                  onFavorite={() => handleFavorite(book)}
                  onRate={(value) => handleRating(book, value)}
                  showRatingButton={true}
                  showFavoriteButton={true}
                  showDetailsLink={true}
                />
              ))}
            </div>
          </section>
        )}

        <section className="recommendation-section">
          <h2 className="section-title">Popular Books</h2>
          <p className="section-description">Trending books loved by readers worldwide</p>
          <div className="books-grid">
            {popularBooks.map((book) => (
              <BookCard
                key={book.id}
                book={book}
                userRating={ratings[book.id] || 0}
                isFavorite={favorites.includes(book.id)}
                onFavorite={() => handleFavorite(book)}
                onRate={(value) => handleRating(book, value)}
                showRatingButton={true}
                showFavoriteButton={true}
                showDetailsLink={true}
              />
            ))}
          </div>
        </section>

        {!userId && (
          <div className="login-prompt">
            <h3>Want better recommendations?</h3>
            <p>Sign in to get personalized book suggestions based on your preferences.</p>
            <a href="/login" className="login-button">
              Sign In
            </a>
          </div>
        )}
      </div>
    </div>
  )
}

export default RecommendedPage